// Utility functions for the application

/**
 * Truncates text to a specified length and adds ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number = 30): string => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

/**
 * Truncates analysis/creative names with context-appropriate lengths
 */
export const truncateAnalysisName = {
  /** For widget display (shorter) */
  widget: (name: string) => truncateText(name, 25),
  /** For list view (medium) */
  list: (name: string) => truncateText(name, 40),
  /** For main title (compact for clean detail page layout) */
  title: (name: string) => truncateText(name, 20),
  /** For table cells (compact) */
  table: (name: string) => truncateText(name, 20),
  /** For detail page headers (balanced length) */
  detail: (name: string) => truncateText(name, 45),
};

/**
 * Formats date strings for display
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return 'Unknown date';
  }
};

/**
 * Formats relative time (e.g., "2h ago", "Yesterday")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export const formatRelativeDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  } catch {
    return 'Unknown date';
  }
}; 